import httpClient from 'api/httpClient';
import {
  SellerConnectCreateSubmissionBody,
  SellerConnectCreateSubmissionResponse,
  SellerConnectSubmission,
  SellerConnectUpdateSubmissionBody,
  SellerConnectScreeningBlock,
  SellerConnectScreeningMode,
  SellerConnectScreeningResponseInput,
  SellerConnectScreeningSession,
  SellerConnectScreeningStatus,
  SellerConnectRuntimeConfig,
  SellerConnectVoiceCall,
  SellerConnectMagicLinkSession,
  SellerConnectChatReply,
  SellerConnectReturnTokenRedeem,
  SellerConnectIm,
  SellerConnectMagicLinkResend,
  SellerConnectSeller,
  SellerConnectSellerFilterOptionField,
  SellerConnectSellersFilters,
  SellerConnectAdminSubmission,
} from 'models/SellerConnectEngine';
import publicHttpClient from 'api/publicHttpClient';
import { PaginatedData, PaginationParams } from 'models/PaginatedData';
import { TableExportParams } from 'models/TableExport';
import { FilePreview } from 'models/File';

const SELLER_CONNECT_ROUTE = 'seller-connect';

// Gate 1 issues a per-submission access token; it unlocks only that submission.
const getSubmissionTokenConfig = (submissionToken?: string) =>
  submissionToken
    ? { headers: { 'X-Submission-Token': submissionToken } }
    : undefined;

function createSubmission(body: SellerConnectCreateSubmissionBody) {
  return httpClient.post<SellerConnectCreateSubmissionResponse>(
    `${SELLER_CONNECT_ROUTE}/submissions`,
    body,
  );
}

function updateSubmission(
  submissionId: string,
  body: SellerConnectUpdateSubmissionBody,
  submissionToken?: string,
) {
  return httpClient.patch<SellerConnectSubmission>(
    `${SELLER_CONNECT_ROUTE}/submissions/${submissionId}`,
    body,
    getSubmissionTokenConfig(submissionToken),
  );
}

function getSubmission(submissionId: string, submissionToken?: string) {
  return httpClient.get<SellerConnectSubmission>(
    `${SELLER_CONNECT_ROUTE}/submissions/${submissionId}`,
    getSubmissionTokenConfig(submissionToken),
  );
}

function getSubmissionIm(submissionId: string) {
  return httpClient.get<SellerConnectIm>(
    `${SELLER_CONNECT_ROUTE}/submissions/${submissionId}/im`,
  );
}

function getSubmissionImPdf(submissionId: string) {
  return httpClient.get<FilePreview>(
    `${SELLER_CONNECT_ROUTE}/submissions/${submissionId}/im/pdf`,
  );
}

function updateSubmissionImDetails(
  submissionId: string,
  responses: SellerConnectScreeningResponseInput[],
) {
  return httpClient.put<SellerConnectIm>(
    `${SELLER_CONNECT_ROUTE}/submissions/${submissionId}/im/details`,
    { responses },
  );
}

function updateSubmissionImSection(
  submissionId: string,
  sectionId: string,
  items: string[],
) {
  return httpClient.put<SellerConnectIm>(
    `${SELLER_CONNECT_ROUTE}/submissions/${submissionId}/im/sections/${sectionId}`,
    { items },
  );
}

function regenerateSubmissionIm(submissionId: string) {
  return httpClient.post<SellerConnectIm>(
    `${SELLER_CONNECT_ROUTE}/submissions/${submissionId}/im/regenerate`,
  );
}

function getScreeningSessions(submissionId: string, submissionToken?: string) {
  return httpClient.get<SellerConnectScreeningSession[]>(
    `${SELLER_CONNECT_ROUTE}/submissions/${submissionId}/screening-sessions`,
    getSubmissionTokenConfig(submissionToken),
  );
}

function startScreeningSession(
  submissionId: string,
  mode: SellerConnectScreeningMode,
  submissionToken?: string,
) {
  return httpClient.post<SellerConnectScreeningSession>(
    `${SELLER_CONNECT_ROUTE}/submissions/${submissionId}/screening-sessions`,
    { mode },
    getSubmissionTokenConfig(submissionToken),
  );
}

function startVoiceCall(
  submissionId: string,
  recordingConsent: boolean,
  submissionToken?: string,
) {
  return httpClient.post<SellerConnectVoiceCall>(
    `${SELLER_CONNECT_ROUTE}/submissions/${submissionId}/voice-call`,
    { recordingConsent },
    getSubmissionTokenConfig(submissionToken),
  );
}

function startChat(
  submissionId: string,
  continuesCall: boolean,
  submissionToken?: string,
) {
  return httpClient.post<SellerConnectScreeningSession>(
    `${SELLER_CONNECT_ROUTE}/submissions/${submissionId}/chat`,
    { continuesCall },
    getSubmissionTokenConfig(submissionToken),
  );
}

function sendChatMessage(
  sessionId: string,
  content: string,
  submissionToken?: string,
) {
  return httpClient.post<SellerConnectChatReply>(
    `${SELLER_CONNECT_ROUTE}/chat-sessions/${sessionId}/messages`,
    { content },
    getSubmissionTokenConfig(submissionToken),
  );
}

function endChat(sessionId: string, submissionToken?: string) {
  return httpClient.post<SellerConnectScreeningSession>(
    `${SELLER_CONNECT_ROUTE}/chat-sessions/${sessionId}/end`,
    undefined,
    getSubmissionTokenConfig(submissionToken),
  );
}

function getScreeningSession(sessionId: string, submissionToken?: string) {
  return httpClient.get<SellerConnectScreeningSession>(
    `${SELLER_CONNECT_ROUTE}/screening-sessions/${sessionId}`,
    getSubmissionTokenConfig(submissionToken),
  );
}

function saveScreeningBlock(
  sessionId: string,
  block: SellerConnectScreeningBlock,
  responses: SellerConnectScreeningResponseInput[],
  submissionToken?: string,
) {
  return httpClient.put<{ saved: boolean }>(
    `${SELLER_CONNECT_ROUTE}/screening-sessions/${sessionId}/blocks/${block}`,
    { responses },
    getSubmissionTokenConfig(submissionToken),
  );
}

function updateScreeningSessionStatus(
  sessionId: string,
  status: SellerConnectScreeningStatus,
  submissionToken?: string,
) {
  return httpClient.patch<SellerConnectScreeningSession>(
    `${SELLER_CONNECT_ROUTE}/screening-sessions/${sessionId}/status`,
    { status },
    getSubmissionTokenConfig(submissionToken),
  );
}

function switchScreeningSessionToText(
  sessionId: string,
  submissionToken?: string,
) {
  return httpClient.post<SellerConnectScreeningSession>(
    `${SELLER_CONNECT_ROUTE}/screening-sessions/${sessionId}/switch-to-text`,
    undefined,
    getSubmissionTokenConfig(submissionToken),
  );
}

function getRuntimeConfig() {
  return httpClient.get<SellerConnectRuntimeConfig>(
    `${SELLER_CONNECT_ROUTE}/config`,
  );
}

function requestMagicLink(email: string, redirectPath: string) {
  return publicHttpClient.post<{ sent: boolean }>(
    `${SELLER_CONNECT_ROUTE}/auth/magic-link`,
    { email, redirectPath },
  );
}

function verifyMagicLink(token: string) {
  return publicHttpClient.post<SellerConnectMagicLinkSession>(
    `${SELLER_CONNECT_ROUTE}/auth/magic-link/verify`,
    { token },
  );
}

function resendMagicLink(token: string) {
  return publicHttpClient.post<SellerConnectMagicLinkResend>(
    `${SELLER_CONNECT_ROUTE}/auth/magic-link/resend`,
    { token },
  );
}

function getMagicLinkResendEmail(token: string) {
  return publicHttpClient.post<{ email: string }>(
    `${SELLER_CONNECT_ROUTE}/auth/magic-link/resend-email`,
    { token },
  );
}

function redeemReturnToken(token: string) {
  return publicHttpClient.post<SellerConnectReturnTokenRedeem>(
    `${SELLER_CONNECT_ROUTE}/return-token/redeem`,
    { token },
  );
}

const SELLER_CONNECT_SELLERS_ROUTE = 'seller-connect-sellers';

function getSellerConnectSellers(
  params?: PaginationParams & SellerConnectSellersFilters,
) {
  return httpClient.get<PaginatedData<SellerConnectSeller>>(
    SELLER_CONNECT_SELLERS_ROUTE,
    { params },
  );
}

function getSellerConnectSellerFilterOptions(
  field: SellerConnectSellerFilterOptionField,
  searchString: string,
) {
  return httpClient.get<string[]>(
    `${SELLER_CONNECT_SELLERS_ROUTE}/options/${field}`,
    { params: { $searchString: searchString } },
  );
}

function exportSellerConnectSellers(
  body: PaginationParams & SellerConnectSellersFilters & TableExportParams,
) {
  const { uniqueId, format, columns, ...params } = body;

  return httpClient.post<FilePreview>(
    `${SELLER_CONNECT_SELLERS_ROUTE}/export`,
    { uniqueId, format, columns },
    { params },
  );
}

function getSellerConnectAdminSubmission(submissionId: string) {
  return httpClient.get<SellerConnectAdminSubmission>(
    `${SELLER_CONNECT_ROUTE}/admin/submissions/${submissionId}`,
  );
}

function getSellerConnectAdminImPdf(submissionId: string) {
  return httpClient.get<FilePreview>(
    `${SELLER_CONNECT_ROUTE}/admin/submissions/${submissionId}/im/pdf`,
  );
}

export default {
  createSubmission,
  updateSubmission,
  getSubmission,
  getSubmissionIm,
  getSubmissionImPdf,
  updateSubmissionImDetails,
  updateSubmissionImSection,
  regenerateSubmissionIm,
  getScreeningSessions,
  startScreeningSession,
  startVoiceCall,
  startChat,
  sendChatMessage,
  endChat,
  getScreeningSession,
  saveScreeningBlock,
  updateScreeningSessionStatus,
  switchScreeningSessionToText,
  getRuntimeConfig,
  requestMagicLink,
  verifyMagicLink,
  resendMagicLink,
  getMagicLinkResendEmail,
  redeemReturnToken,
  getSellerConnectSellers,
  getSellerConnectSellerFilterOptions,
  exportSellerConnectSellers,
  getSellerConnectAdminSubmission,
  getSellerConnectAdminImPdf,
};
